<template>
  <div class="sell-order-container">
    <div class="search-container">
      <el-input
        v-model="searchKeyword"
        :placeholder="$t('sellOrder.search_placeholder')"
        class="search-input"
        @keyup.enter.native="searchOrders"
      >
        <i slot="prefix" class="el-icon-search" />
      </el-input>
    </div>

    <div class="tab-container">
      <div
        v-for="(tab, index) in tabs"
        :key="index"
        :class="['tab-item', { active: activeTab === tab.value }]"
        @click="switchTab(tab.value)"
      >
        <span :class="['tab-text', { active: activeTab === tab.value }]">{{
          tab.label
        }}</span>
      </div>
    </div>

    <div class="order-list">
      <div
        v-for="(order, index) in orderList"
        :key="index"
        class="order-item"
        @click="navigateToDetails(order)"
      >
        <div class="order-header">
          <span class="order-id"
            >{{ $t("sellOrder.order_id") }} {{ order.orderNo }}</span
          >
          <span :class="['order-status', order.status]">{{
            getStatusText(order.status)
          }}</span>
        </div>

        <div class="product-info">
          <img
            class="product-icon"
            :src="order.coinIcon"
            @error="onIconError(order)"
          />
          <div class="product-detail">
            <span class="product-name">{{ order.projectName }}</span>
            <span class="product-tag">
              {{
                order.saleType === 1
                  ? $t("mall.pre_sale")
                  : $t("mall.open_sale")
              }}
            </span>
          </div>
          <span class="product-price"
            >{{ (order.payAmountUsd || 0).toLocaleString() }} USD</span
          >
        </div>

        <div class="order-detail">
          <div class="detail-row">
            <span class="detail-label">{{
              $t("sellOrder.subscription_shares")
            }}</span>
            <span class="detail-value"
              >{{ (order.shares || 0).toLocaleString() }}
              {{ $t("mall.shares_unit") }}</span
            >
          </div>
          <div class="detail-row">
            <span class="detail-label">{{
              $t("sellOrder.payment_price")
            }}</span>
            <span class="detail-value"
              >{{ (order.priceUsd || 0).toLocaleString() }} USD/{{
                $t("mall.shares_unit")
              }}</span
            >
          </div>
          <div class="detail-row">
            <span class="detail-label">{{
              $t("sellOrder.total_payment")
            }}</span>
            <span class="detail-value" v-if="order.payAssetType === 'HKD'">{{ (order.payAmountHkd || 0).toLocaleString() }} {{ order.payAssetType }}</span>
            <span class="detail-value" v-else>{{ (order.payAmountUsd || 0).toLocaleString() }} USD</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">{{ $t("sellOrder.order_time") }}</span>
            <span class="detail-value">{{
              formatTimestamp(order.createTime) || "--"
            }}</span>
          </div>
        </div>

        <div v-if="order.status === 'pending_pay'" class="order-actions">
          <el-button
            type="default"
            size="small"
            class="action-btn cancel-btn"
            @click.stop="cancelOrder(order)"
          >
            {{ $t("sellOrder.cancel_order") }}
          </el-button>
          <el-button
            type="primary"
            size="small"
            class="action-btn pay-btn"
            @click.stop="payOrder(order)"
          >
            {{ $t("sellOrder.go_pay") }}
          </el-button>
        </div>
      </div>

    </div>

    <div class="pagination-container">
      <el-pagination
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        :current-page="pageNumber"
        :page-sizes="[10, 20, 50]"
        :page-size="pageSize"
        layout="total, sizes, prev, pager, next, jumper"
        :total="total"
      />
    </div>

    <el-dialog :visible.sync="showPayModal" width="500px" title="支付">
      <div class="pay-modal-content">
        <div class="pay-amount-section">
          <span class="pay-label">{{ $t("sellOrder.pay_amount") }}</span>
          <span class="pay-amount">{{ (currentOrder && currentOrder.payAmountUsd ? currentOrder.payAmountUsd : 0).toLocaleString() }} USD</span>
        </div>

        <div class="pay-method-section">
          <span class="section-title">{{ $t("sellOrder.pay_method") }}</span>
          <div
            v-for="method in payMethods"
            :key="method.value"
            :class="[
              'pay-method-item',
              { active: selectedPayMethod === method.value },
            ]"
            @click="selectedPayMethod = method.value"
          >
            <div class="method-radio">
              <div
                v-if="selectedPayMethod === method.value"
                class="radio-inner"
              ></div>
            </div>
            <div class="method-info">
              <span class="method-name">{{ method.label }}</span>
              <span class="method-balance"
                >{{ $t("sellOrder.balance") }}: {{ method.balance }}
                {{ method.value }}</span
              >
            </div>
            <span class="recharge-btn" @click.stop="handleRecharge(method.value)">{{ $t("sellOrder.recharge") }}</span>
          </div>
        </div>

        <div class="password-section">
          <div class="password-label-row">
            <span class="section-title">{{ $t("sellOrder.trade_password") }}</span>
            <span class="set-password-link" @click="goToSecuritySettings">{{ $t("sellOrder.set_password") }}</span>
          </div>
          <el-input
            type="password"
            v-model="tradePassword"
            :placeholder="$t('sellOrder.password_placeholder')"
            class="password-input"
            maxlength="6"
          />
        </div>

        <div class="modal-footer">
          <div :class="['submit-btn', { disabled: !isBalanceSufficient }]" @click="confirmPayment">
            {{ $t("sellOrder.confirm_pay") }}
          </div>
        </div>
      </div>
    </el-dialog>

    <el-dialog
      :visible.sync="showSuccessModal"
      width="400px"
      :show-close="false"
    >
      <div class="success-modal-content">
        <div class="success-icon">OK</div>
        <span class="success-title">{{ $t("sellOrder.pay_success") }}</span>
        <div class="success-btn" @click="handleSuccessClose">
          {{ $t("sellOrder.confirm") }}
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { mapState } from "vuex";
export default {
  data() {
    return {
      searchKeyword: "",
      activeTab: "",
      tabs: [
        { label: this.$t("sellOrder.all"), value: "" },
        { label: this.$t("sellOrder.completed"), value: "completed" },
        {
          label: this.$t("sellOrder.pending_distribution"),
          value: "pending_distribute",
        },
        { label: this.$t("sellOrder.pending_payment"), value: "pending_pay" },
        { label: this.$t("sellOrder.cancelled"), value: "canceled" },
      ],
      orderList: [],
      loading: false,
      pageNumber: 1,
      pageSize: 20,
      total: 0,
      showPayModal: false,
      showSuccessModal: false,
      currentOrder: null,
      selectedPayMethod: "USD",
      tradePassword: "",
      payMethods: [
        { label: "USD", value: "USD", balance: 0 },
        { label: "HKD", value: "HKD", balance: 0 },
      ],
    };
  },
  computed: {
    ...mapState({
      user: "user",
      errorCode: "errorCode",
    }),
    isBalanceSufficient() {
      const hasAnyBalance = this.payMethods.some(method => Number(method.balance) > 0);
      if (!hasAnyBalance) {
        return false;
      }
      const method = this.payMethods.find(m => m.value === this.selectedPayMethod);
      if (method && this.currentOrder) {
        return Number(method.balance) >= Number(this.currentOrder.payAmountUsd || 0);
      }
      return false;
    },
  },
  mounted() {
    this.fetchOrderList();
  },
  methods: {
    onIconError(order) {
      order.coinIcon = null;
    },
    navigateToDetails(order) {
      this.$router.push({
        path: "/sellOrderDetails",
        query: { orderNo: order.orderNo },
      });
    },
    getStatusText(status) {
      const statusMap = {
        completed: this.$t("sellOrder.status_completed"),
        pending_distribute: this.$t("sellOrder.status_pending_distribution"),
        pending_pay: this.$t("sellOrder.status_pending_payment"),
        canceled: this.$t("sellOrder.status_cancelled"),
      };
      return statusMap[status] || "";
    },
    formatTimestamp(timestamp) {
      if (!timestamp) return "";
      const date = new Date(timestamp);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      const seconds = String(date.getSeconds()).padStart(2, "0");
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    },
    async fetchOrderList() {
      if (this.loading) {
        return;
      }
      this.loading = true;
      try {
        const params = {
          status: this.activeTab === "all" ? "" : this.activeTab,
          keyword: this.searchKeyword,
          pageNumber: this.pageNumber,
          pageSize: this.pageSize,
          limit: this.pageSize,
        };
        const res = await this.$axios.post("/mall/orders", params);
        if (res.data.code === this.errorCode.ok && res.data.data) {
          this.orderList = res.data.data.rows || [];
          this.total = res.data.data.total || 0;
        }
      } catch (error) {
        this.$message.error(this.$t("common.error"));
      } finally {
        this.loading = false;
      }
    },
    switchTab(tab) {
      if (this.activeTab !== tab) {
        this.activeTab = tab;
        this.pageNumber = 1;
        this.orderList = [];
        this.fetchOrderList();
      }
    },
    searchOrders() {
      this.pageNumber = 1;
      this.orderList = [];
      this.fetchOrderList();
    },
    handleSizeChange(val) {
      this.pageSize = val;
      this.pageNumber = 1;
      this.fetchOrderList();
    },
    handleCurrentChange(val) {
      this.pageNumber = val;
      this.fetchOrderList();
    },
    cancelOrder(order) {
      this.$confirm(
        this.$t("sellOrder.cancel_confirm"),
        this.$t("sellOrder.cancel_tip"),
        {
          confirmButtonText: this.$t("sellOrder.confirm"),
          cancelButtonText: this.$t("sellOrder.cancel"),
          type: "warning",
        },
      )
        .then(async () => {
          try {
            const cancelRes = await this.$axios.post("/mall/order/cancel", {
              orderNo: order.orderNo,
            });
            if (cancelRes.data.code === this.errorCode.ok) {
              this.$message.success(this.$t("sellOrder.cancel_success"));
              this.fetchOrderList();
            } else {
              this.$message.error(
                cancelRes.data.msg || this.$t("sellOrder.cancel_fail"),
              );
            }
          } catch (error) {
            this.$message.error(this.$t("sellOrder.cancel_fail"));
          }
        })
        .catch(() => {});
    },
    payOrder(order) {
      this.currentOrder = order;
      this.selectedPayMethod = "USD";
      this.tradePassword = "";
      this.showPayModal = true;
      this.loadUserAssets();
    },
    loadUserAssets() {
      this.$axios
        .post("/userAssets/queryAssetList", {
          accountType: "spot",
          legalCurrency: "HKD",
        })
        .then((res) => {
          if (
            res.data.code === this.errorCode.ok &&
            res.data.data &&
            res.data.data.userAssetRsps
          ) {
            const usdtAsset = res.data.data.userAssetRsps.find(item => item.assetType === 'USDT');
            if (usdtAsset) {
              const usdMethod = this.payMethods.find(item => item.value === 'USD');
              if (usdMethod) {
                usdMethod.balance = usdtAsset.balanceAvailable || 0;
              }
            }

            const hkdAsset = res.data.data.userAssetRsps.find(item => item.assetType === 'HKD');
            if (hkdAsset) {
              const hkdMethod = this.payMethods.find(item => item.value === 'HKD');
              if (hkdMethod) {
                hkdMethod.balance = hkdAsset.balanceAvailable || 0;
              }
            }
          }
        })
        .catch((err) => {
          console.log("获取币种列表失败:", err);
        });
    },
    handleRecharge(methodValue) {
      this.showPayModal = false;
      this.$emit("recharge", methodValue);
    },
    goToSecuritySettings() {
      this.$router.push({ path: '/user' });
    },
    async confirmPayment() {
      if (!this.tradePassword || this.tradePassword.length < 6) {
        this.$message.warning(this.$t("sellOrder.password_required"));
        return;
      }

      if (!this.isBalanceSufficient) {
        this.$message.warning(this.$t("sellOrder.insufficient_balance"));
        return;
      }

      try {
        const payRes = await this.$axios.post("/mall/order/pay", {
          orderNo: this.currentOrder.orderNo,
          payAssetType: this.selectedPayMethod,
          tranPwd: this.tradePassword,
        });
        if (payRes.data.code === this.errorCode.ok) {
          this.showPayModal = false;
          this.showSuccessModal = true;
          this.tradePassword = "";
          this.fetchOrderList();
        } else {
          this.$message.error(payRes.data.msg || this.$t("sellOrder.pay_fail"));
        }
      } catch (error) {
        this.$message.error(this.$t("sellOrder.pay_fail"));
      }
    },
    handleSuccessClose() {
      this.showSuccessModal = false;
    },
  },
};
</script>

<style lang="less" scoped>
.sell-order-container {
  padding: 20px;
  background: #ffffff;
  border-radius: 16px;
}

.search-container {
  margin-bottom: 20px;

  .search-input {
    width: 100%;
  }
  /deep/ .el-input__prefix {
    display: flex;
    margin-left: 4px;
    align-items: center;
  }
}

.tab-container {
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid #f0f0f0;

  .tab-item {
    padding: 10px 15px;
    cursor: pointer;
    border-radius: 8px;
    transition: all 0.3s;

    &.active {
      background: #e1eaff;

      .tab-text {
        color: #4063ff;
        font-weight: 600;
      }
    }

    .tab-text {
      font-size: 14px;
      color: #666666;
    }
  }
}

.order-list {
  .order-item {
    padding: 20px;
    margin-bottom: 16px;
    background: #f9fafb;
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
      background: #f0f2f5;
    }
  }

  .order-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;

    .order-id {
      font-size: 14px;
      color: #666666;
    }

    .order-status {
      font-size: 14px;
      font-weight: 600;

      &.completed {
        color: #52c41a;
      }

      &.pending_distribute {
        color: #faad14;
      }

      &.pending_pay {
        color: #ff4d4f;
      }

      &.canceled {
        color: #999999;
      }
    }
  }

  .product-info {
    display: flex;
    align-items: center;
    margin-bottom: 16px;
    gap: 12px;

    .product-icon {
      width: 48px;
      height: 48px;
      border-radius: 8px;
      background: #f0f0f0;
    }

    .product-detail {
      flex: 1;

      .product-name {
        display: block;
        font-size: 16px;
        font-weight: 600;
        color: #333333;
        margin-bottom: 4px;
      }

      .product-tag {
        font-size: 12px;
        color: #999999;
      }
    }

    .product-price {
      font-size: 18px;
      font-weight: 700;
      color: #333333;
    }
  }

  .order-detail {
    .detail-row {
      display: flex;
      justify-content: space-between;
      margin-bottom: 8px;

      &:last-child {
        margin-bottom: 0;
      }

      .detail-label {
        font-size: 14px;
        color: #999999;
      }

      .detail-value {
        font-size: 14px;
        color: #333333;
      }
    }
  }

  .order-actions {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid #f0f0f0;

    .action-btn {
      padding: 8px 20px;
      border-radius: 8px;

      &.cancel-btn {
        border-color: #d9d9d9;
        color: #666666;
      }

      &.pay-btn {
        background: #4063ff;
        border-color: #4063ff;
        color: #ffffff;
      }
    }
  }

  .load-more {
    text-align: center;
    padding: 20px;

    .load-more-text {
      font-size: 14px;
      color: #999999;
    }
  }
}

.pay-modal-content {
  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;

    .modal-title {
      font-size: 18px;
      font-weight: 600;
      color: #333333;
    }

    .modal-close {
      font-size: 24px;
      color: #999999;
      cursor: pointer;
    }
  }

  .pay-amount-section {
    text-align: center;
    margin-bottom: 24px;

    .pay-label {
      display: block;
      font-size: 14px;
      color: #999999;
      margin-bottom: 8px;
    }

    .pay-amount {
      font-size: 32px;
      font-weight: 700;
      color: #333333;
    }
  }

  .pay-method-section {
    margin-bottom: 20px;

    .section-title {
      display: block;
      font-size: 14px;
      color: #333333;
      margin-bottom: 12px;
    }

    .pay-method-item {
      display: flex;
      align-items: center;
      padding: 12px 16px;
      margin-bottom: 8px;
      background: #f9fafb;
      border-radius: 8px;
      cursor: pointer;
      transition: all 0.2s;

      &.active {
        background: #e1eaff;
        border: 1px solid #4063ff;
      }

      .method-radio {
        width: 20px;
        height: 20px;
        border: 2px solid #d9d9d9;
        border-radius: 50%;
        margin-right: 12px;
        display: flex;
        align-items: center;
        justify-content: center;

        .radio-inner {
          width: 10px;
          height: 10px;
          background: #4063ff;
          border-radius: 50%;
        }
      }

      .method-info {
        flex: 1;

        .method-name {
          display: block;
          font-size: 14px;
          color: #333333;
        }

        .method-balance {
          font-size: 12px;
          color: #999999;
        }
      }

      .recharge-btn {
        font-size: 14px;
        color: #4063ff;
        cursor: pointer;
      }
    }
  }

  .password-section {
    margin-bottom: 24px;

    .password-label-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 12px;
    }

    .section-title {
      font-size: 14px;
      color: #333333;
    }

    .set-password-link {
      font-size: 14px;
      color: #4063ff;
      cursor: pointer;
    }

    .password-input {
      width: 100%;
    }
  }

  .modal-footer {
    .submit-btn {
      width: 100%;
      height: 48px;
      line-height: 48px;
      text-align: center;
      background: #4063ff;
      color: #ffffff;
      font-size: 16px;
      font-weight: 600;
      border-radius: 8px;
      cursor: pointer;
      transition: all 0.2s;

      &:hover {
        background: #3050e0;
      }

      &.disabled {
        opacity: 0.5;
        pointer-events: none;
      }
    }
  }
}

.success-modal-content {
  text-align: center;
  padding: 40px 20px;

  .success-icon {
    width: 80px;
    height: 80px;
    line-height: 80px;
    background: #52c41a;
    color: #ffffff;
    font-size: 36px;
    font-weight: bold;
    border-radius: 50%;
    margin: 0 auto 20px;
  }

  .success-title {
    display: block;
    font-size: 20px;
    font-weight: 600;
    color: #333333;
    margin-bottom: 24px;
  }

  .success-btn {
    display: inline-block;
    padding: 12px 40px;
    background: #4063ff;
    color: #ffffff;
    font-size: 16px;
    font-weight: 600;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
      background: #3050e0;
    }
  }
}

.pagination-container {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #f0f0f0;

  ::v-deep .el-pagination {
    .el-pagination__total {
      margin-right: 16px;
      font-size: 14px;
      color: #666666;
    }

    .el-pagination__sizes {
      margin-right: 16px;
    }

    .el-pager li {
      margin: 0 4px;
      min-width: 32px;
      height: 32px;
      line-height: 32px;
      border-radius: 6px;

      &.active {
        background: #4063ff;
        color: #ffffff;
      }
    }

    .btn-prev,
    .btn-next {
      min-width: 32px;
      height: 32px;
      line-height: 32px;
      border-radius: 6px;
    }
  }
}
</style>
